// Dashboard Constants and Utilities
// Use current browser origin for API calls - supports custom domains in production
export const BACKEND_URL = window.location.origin;
export const API = `${BACKEND_URL}/api`;

export const PLATFORMS = [
  { id: "instagram", name: { en: "Instagram", ko: "인스타그램", zh: "Instagram" }, color: "#833AB4" },
  { id: "tiktok", name: { en: "TikTok", ko: "틱톡", zh: "TikTok" }, color: "#25282B" },
  { id: "youtube", name: { en: "YouTube", ko: "유튜브", zh: "YouTube" }, color: "#FF0000" },
  { id: "weibo", name: { en: "Weibo", ko: "웨이보", zh: "微博" }, color: "#FF8C00" },
  { id: "xiaohongshu", name: { en: "Xiaohongshu", ko: "샤오홍슈", zh: "小红书" }, color: "#FACC15" },
  { id: "bilibili", name: { en: "Bilibili", ko: "빌리빌리", zh: "哔哩哔哩" }, color: "#F47295" },
  { id: "douyin", name: { en: "Douyin", ko: "더우인", zh: "抖音" }, color: "#25F4EE" },
];

export const METRICS = [
  { id: "views", label: "Views" },
  { id: "likes", label: "Likes" },
  { id: "comments", label: "Comments" },
  { id: "engagement_rate", label: "Engagement Rate" },
];

export const LANGUAGE_COLORS = {
  en: "#3b82f6",
  ko: "#ef4444",
  zh: "#10b981",
  ja: "#f59e0b",
  other: "#8b5cf6",
};

export const SENTIMENT_COLORS = {
  positive: "#10b981",
  neutral: "#6b7280",
  negative: "#ef4444",
};

export const getPlatformName = (platform, lang) => {
  const found = PLATFORMS.find((p) => p.id === platform.id || p.id === platform);
  if (found && typeof found.name === "object") {
    return found.name[lang] || found.name.en;
  }
  return platform.name;
};

export const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || "0";
};

export const getPlatformBadgeClass = (platform) => `badge-${platform}`;

// Platforms without views data (show N/A for views=0)
export const NO_VIEWS_PLATFORMS = ["weibo", "xiaohongshu", "douyin"];

// Chinese platforms (for likes-based sorting)
export const CHINESE_PLATFORMS = ["weibo", "xiaohongshu", "bilibili", "douyin"];

// Per-platform metric availability (false = not available from API)
export const PLATFORM_METRIC_AVAILABILITY = {
  youtube:     { views: true, likes: true, comments: true, shares: false, saves: false },
  tiktok:      { views: true, likes: true, comments: true, shares: true,  saves: true  },
  instagram:   { views: true, likes: true, comments: true, shares: true,  saves: false },
  xiaohongshu: { views: false, likes: true, comments: true, shares: true,  saves: true  },
  weibo:       { views: true, likes: true, comments: true, shares: true,  saves: false },
  bilibili:    { views: true, likes: true, comments: true, shares: true,  saves: true  },
  douyin:      { views: true, likes: true, comments: true, shares: true,  saves: true  },
};

// Check if a metric is available for a given platform (or set of platforms)
export const isMetricAvailable = (metric, platforms) => {
  if (!platforms || platforms.length === 0) return true; // All platforms = show metric
  return platforms.some(p => PLATFORM_METRIC_AVAILABILITY[p]?.[metric] !== false);
};

// Check if a metric is unavailable for ALL given platforms
export const isMetricUnavailableForAll = (metric, platforms) => {
  if (!platforms || platforms.length === 0) return false;
  return platforms.every(p => PLATFORM_METRIC_AVAILABILITY[p]?.[metric] === false);
};

export const isChinesePlatform = (platform) => CHINESE_PLATFORMS.includes(platform);

export const hasNoViewsData = (platform) => NO_VIEWS_PLATFORMS.includes(platform);

// Platform colors for badges/identity
export const PLATFORM_COLORS = {
  instagram: "#833AB4",
  tiktok: "#25282B",
  youtube: "#FF0000",
  weibo: "#FF8C00",
  xiaohongshu: "#FACC15",
  bilibili: "#F47295",
  douyin: "#25F4EE",
};

// Chart-optimized colors (visible on both light/dark backgrounds)
export const PLATFORM_CHART_COLORS = {
  instagram: "#833AB4",
  tiktok: "#6B7280",
  youtube: "#FF0000",
  weibo: "#FF8C00",
  xiaohongshu: "#D4A017",
  bilibili: "#F47295",
  douyin: "#25F4EE",
};
